import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

export type TrendVariant = 'chip' | 'icon';
export type TrendMetric = 'citations' | 'views';

export type TrendAdornmentProps = {
  variant: TrendVariant;
  pct: number | null;
  metric?: TrendMetric;
};

type Direction = 'up' | 'down';

const ChipColumn = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  row-gap: 3px;
`;

const IconPill = styled.span<{ direction: Direction }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  border-radius: var(--border-radius-radius-round, 104px);
  color: var(--text-white, #ffffff);

  &.direction-up {
    background: var(--success-dark, #05b01c);
  }

  &.direction-down {
    background: var(--error-dark, #b60000);
  }
`;

const PctText = styled.span`
  @import 'stylesheets/design_system/font';
  @include sans-serif;

  color: var(--text-primary, #141413);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
`;

const MetricIconWrap = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  color: var(--text-primary, #141413);
`;

const PILL_ICON_NAMES: Record<Direction, string> = {
  up: 'trending_up',
  down: 'trending_down',
};

const METRIC_ICON_NAMES: Record<TrendMetric, string> = {
  citations: 'bar_chart_4_bars',
  views: 'trending_up',
};

function roundPct(pct: number): number {
  return Number(pct.toFixed(1)) + 0;
}

export function formatTrendPct(pct: number): string {
  const rounded = roundPct(pct);
  const sign = rounded >= 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}%`;
}

function trendDirection(pct: number): Direction {
  return roundPct(pct) >= 0 ? 'up' : 'down';
}

const TrendAdornment = ({
  variant,
  pct,
  metric = 'views',
}: TrendAdornmentProps) => {
  if (pct == null) {
    return null;
  }

  if (variant === 'chip') {
    const direction = trendDirection(pct);
    return (
      <ChipColumn data-testid="trend-chip" data-direction={direction}>
        <IconPill direction={direction}>
          <Icon
            name={PILL_ICON_NAMES[direction]}
            size={20}
            role="img"
            aria-label={`Trend: ${direction}`}
          />
        </IconPill>
        <PctText data-testid="trend-chip-pct">{formatTrendPct(pct)}</PctText>
      </ChipColumn>
    );
  }

  return (
    <MetricIconWrap data-testid="trend-icon" data-metric={metric}>
      <Icon name={METRIC_ICON_NAMES[metric]} size={22} aria-hidden />
    </MetricIconWrap>
  );
};

export default TrendAdornment;
